// evolveChat — the total half of the chat decider. Split out of
// `app/chatDecider.ts` (C1 hotspot decomposition; verbatim).
//
// State × Event → State. Trusts payloads; no Result, no throw. Every
// session-scoped event routes through `updateChatSession` (creates an empty
// session on first touch, exactly as the pre-M1 closures did). Ends in
// `exhaustSwitchCase`. Purity: no I/O, no clock, no React.

import * as TurnLiveness from "@bilda/app/turnLiveness";
import { exhaustSwitchCase } from "@bilda/utils";
import { interruptionOfRefusal } from "../../app/interruption.js";
import {
    addPendingGrounding,
    clearPendingGrounding,
    clearPendingSituation,
    type PendingGrounding,
    removePendingGrounding,
} from "../../app/pendingGrounding.js";
import {
    dropPendingInformationRequests,
    foldTurnStreamEvent,
    sweepBuildingToStopped,
} from "../../app/turnStream.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type { ChatEvent } from "./event.js";
import { recallNewer, recallOlder } from "./historyNav.js";
import {
    afterResumeActivityId,
    afterTurnLocalIds,
    type ChatSession,
    settleUploadChip,
    updateChatSession,
} from "./model.js";
import type { ChatState } from "./state.js";
import {
    finalizeStreamedAgentEntry,
    mergeInFlightTranscript,
    replaceOrAppend,
    withoutUnfinishedRows,
} from "./transcript.js";

export const evolveChat = (state: ChatState, event: ChatEvent): ChatState => {
    switch (event.type) {
        case "turn_started": {
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_began",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    conversation: [
                        ...session.conversation,
                        {
                            type: "author",
                            id: event.entryId,
                            text: event.text,
                            // Optional: omit entirely when there are no
                            // @-references, so a plain turn's author entry keeps
                            // its prior shape.
                            ...(event.mentions.length > 0
                                ? { mentions: event.mentions }
                                : {}),
                        },
                    ],
                    draft: "",
                    lastSent: { type: "last_sent_text", text: event.text },
                    turnError: null,
                    // Projected off the liveness reducer (true after turn_began).
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    liveActivity: null,
                    outlineDraft: null,
                    // Sending resets the composer's history cursor to the live
                    // (now-empty) draft — a fresh turn starts a fresh recall (A-11).
                    historyNav: { type: "at_draft" },
                    nextLocalId: afterTurnLocalIds(session),
                })),
                liveness,
                // The draft's mention chips are spent with it (D4); each source
                // was already grounded when it was picked.
                composerMentions: [],
            };
        }
        case "turn_followed": {
            // #100: a turn this page did not start, followed after a reload.
            // Drafting on, nothing sent from here, so Retry stays as it was.
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_began",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    turnError: null,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    liveActivity: null,
                    outlineDraft: null,
                    nextLocalId: afterResumeActivityId(session),
                })),
                liveness,
            };
        }
        case "turn_resumed": {
            // F113: the resume after a form answer. Drafting on, failure
            // cleared, Retry armed to re-send the resume; no author bubble,
            // and `draft` / `historyNav` stay as they were.
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_began",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    lastSent: {
                        type: "last_sent_form_resume",
                        requestId: event.requestId,
                        text: event.text,
                    },
                    turnError: null,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    liveActivity: null,
                    outlineDraft: null,
                    nextLocalId: afterResumeActivityId(session),
                })),
                liveness,
            };
        }
        case "form_answer_began": {
            // Mark drafting the instant the form is answered (F99) so the async
            // answer→resume window refuses a concurrent submit. No transcript
            // change.
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_began",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    liveActivity: null,
                    outlineDraft: null,
                })),
                liveness,
            };
        }
        case "turn_stream_folded":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                // D51: the live row shows the latest activity.
                ...(event.event.type === "activity"
                    ? { liveActivity: event.event.activity }
                    : {}),
                ...(event.event.type === "outline_draft"
                    ? { outlineDraft: event.event.draft }
                    : {}),
                conversation: foldTurnStreamEvent(
                    session.conversation,
                    event.event,
                    event.ids,
                ),
            }));
        case "turn_reply_landed": {
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_landed",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    // Replace-or-append by the reply's OWN id (+ type — see
                    // `replaceOrAppend`): the durable reply reuses the streamed
                    // message's app-minted id, so dropping the entry that shares
                    // it removes exactly the live bubble the deltas grew (and is
                    // a no-op when nothing streamed). This is what keeps the
                    // streamed bubble and the durable reply from both rendering
                    // — the duplicate-message class of bug (10d88b34). `reply`
                    // is OPTIONAL (F43b): a turn that ends on a typed epilogue
                    // alone has nothing to fold here — skip the append entirely
                    // rather than pass a synthetic empty entry through.
                    // Turn-end backstop (A-14 / I3): sweep any still-building card
                    // to stopped so a card can never outlive its turn — covers a
                    // build whose `build_stopped` delta was lost in transport.
                    // F53: also drop any form-PENDING placeholder — the finished
                    // form (if one landed) superseded it live; a reply that landed
                    // without a form must not leave "preparing a form" standing.
                    conversation: dropPendingInformationRequests(
                        sweepBuildingToStopped(
                            event.reply === undefined
                                ? session.conversation
                                : replaceOrAppend(
                                      session.conversation,
                                      event.reply,
                                  ),
                        ),
                    ),
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                })),
                liveness,
            };
        }
        case "turn_failed": {
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_failed",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    turnError: event.failure,
                    heldSend: { type: "no_held_send" },
                    // Turn-end backstop (A-14 / I3): a failed turn does NOT reload
                    // the transcript (reread_world fires only on reply), so a
                    // stranded build card would otherwise spin forever here. F53:
                    // likewise drop a form-PENDING placeholder — the form will
                    // never land now, so it must not read as a frozen "preparing".
                    conversation: dropPendingInformationRequests(
                        sweepBuildingToStopped(session.conversation),
                    ),
                })),
                liveness,
            };
        }
        case "turn_cut_short": {
            const cancelled = new Set(event.cancelledMessageIds);
            // Settle AND fence via the shared reducer: a refused request (a dead
            // connection or any other refusal) ends the turn (drafting off) and fences the finalized bubbles so a stray
            // buffered delta can never re-grow one. This arm previously forgot to
            // clear `drafting` — the D-0 bug, masked only because `turn_failed`
            // folds first; deriving off the reducer makes it correct on its own.
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_lost_connection",
                conversationId: event.conversationId,
                cancelledMessageIds: event.cancelledMessageIds,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    // Finalize each live streaming bubble (drop `streamSeq`, keep
                    // its streamed-so-far text) — same salvage as `turn_stopped`,
                    // so the half-written reply isn't lost. Then append the quiet
                    // system row so the accidental loss shows in the transcript.
                    // Turn-end backstop (A-14 / I3): sweep still-building cards
                    // to stopped — a dead connection can never deliver the
                    // app's `build_stopped` delta, so this is the only signal.
                    // F53: also drop a form-PENDING placeholder (same reasoning —
                    // the form can never land on a dead connection).
                    conversation: dropPendingInformationRequests(
                        sweepBuildingToStopped([
                            ...session.conversation.map((entry) =>
                                entry.type === "agent" &&
                                cancelled.has(entry.id)
                                    ? finalizeStreamedAgentEntry(entry)
                                    : entry,
                            ),
                            {
                                type: "system",
                                id: event.entryId,
                                kind: interruptionOfRefusal(event.error),
                            },
                        ]),
                    ),
                })),
                liveness,
            };
        }
        case "turn_stopped": {
            const cancelled = new Set(event.cancelledMessageIds);
            // Settle AND fence via the shared reducer (the fenced ids let
            // decideChat drop a late delta/reply reusing one of them).
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_stopped",
                conversationId: event.conversationId,
                cancelledMessageIds: event.cancelledMessageIds,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    // A send held for uploads is dropped with its stopped
                    // turn. One held for the durable start still goes out
                    // once the start lands, exactly as the former single
                    // start-then-send chain did; its reply is fenced.
                    heldSend:
                        session.heldSend.type === "awaiting_uploads"
                            ? { type: "no_held_send" }
                            : session.heldSend,
                    // Finalize each live streaming bubble for this turn: drop its
                    // `streamSeq` so it becomes a durable entry, not a growing one
                    // (its streamed-so-far text is kept). The fence stops any late
                    // delta from reaching it regardless; this makes the
                    // "still-streaming after stop" state unrepresentable too.
                    // Turn-end backstop (A-14 / I3): also sweep any still-building
                    // card to stopped — a stopped turn leaves no card spinning.
                    // F53: and drop a form-PENDING placeholder for the same reason.
                    conversation: dropPendingInformationRequests(
                        sweepBuildingToStopped(
                            session.conversation.map((entry) =>
                                entry.type === "agent" &&
                                cancelled.has(entry.id)
                                    ? finalizeStreamedAgentEntry(entry)
                                    : entry,
                            ),
                        ),
                    ),
                })),
                liveness,
            };
        }
        case "waiting_message_posted":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                conversation: [
                    ...session.conversation,
                    {
                        type: "author",
                        id: event.entryId,
                        text: event.text,
                        status: { type: "waiting" },
                    },
                ],
                draft: "",
                nextLocalId: afterTurnLocalIds(session),
            }));
        case "waiting_message_confirmed":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                conversation: session.conversation.some(
                    (e) => e.id === event.messageId,
                )
                    ? session.conversation.filter((e) => e.id !== event.entryId)
                    : session.conversation.map((e) =>
                          e.id === event.entryId
                              ? { ...e, id: event.messageId }
                              : e,
                      ),
            }));
        case "waiting_message_returned":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                conversation: session.conversation.filter(
                    (e) => e.id !== event.entryId,
                ),
                draft: withLines(session.draft, [event.text]),
            }));
        case "waiting_texts_drafted":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                draft: withLines(session.draft, event.texts),
            }));
        case "draft_edited":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                draft: event.text,
                turnError: null,
            }));
        case "older_message_recalled":
            return withSession(state, event.conversationId, recallOlder);
        case "newer_message_recalled":
            return withSession(state, event.conversationId, recallNewer);
        case "opening_move_started": {
            // An opening move is a turn for liveness — it begins drafting.
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_began",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                    liveActivity: null,
                    outlineDraft: null,
                })),
                liveness,
            };
        }
        case "opening_move_replied": {
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_landed",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    // Same replace-or-append discipline: if the opening-move deltas
                    // interleaved onto an open stream and grew a bubble under this
                    // reply's id, the durable reply supplants it rather than doubling.
                    // `reply` is OPTIONAL (F43b) — an opening move landing on a
                    // typed epilogue alone has nothing to fold; skip the append.
                    conversation:
                        event.reply === undefined
                            ? session.conversation
                            : replaceOrAppend(
                                  session.conversation,
                                  event.reply,
                              ),
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                })),
                liveness,
            };
        }
        case "opening_move_was_declined":
        case "opening_move_had_error": {
            // Liveness treats both opening-move terminals as a plain settle — it
            // does not branch on declined-vs-error (the transcript/toast do).
            const liveness = TurnLiveness.evolve(state.liveness, {
                type: "turn_landed",
                conversationId: event.conversationId,
            });
            return {
                ...withSession(state, event.conversationId, (session) => ({
                    ...session,
                    drafting: TurnLiveness.isDrafting(
                        liveness,
                        event.conversationId,
                    ),
                })),
                liveness,
            };
        }
        case "conversation_transcript_was_loaded":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                // A stale/empty reload must NEVER drop an in-flight optimistic
                // turn: a post-reply reload can race the NEXT send, resolving
                // before that turn is persisted. (The visibility load, which
                // races the first send of a nascent conversation, is
                // `conversation_was_loaded` and always merges — D9.)
                // Replacing outright would strand the "drafting" indicator over a
                // blank transcript (dogfood P1.4). While a turn is in flight,
                // MERGE; once it has settled the load is authoritative and
                // replaces outright.
                conversation: session.drafting
                    ? mergeInFlightTranscript(
                          withoutUnfinishedRows(event.entries),
                          session.conversation,
                      )
                    : event.entries,
            }));
        case "conversation_profile_was_loaded":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                situationProfile: event.profile,
            }));
        case "conversation_was_grounded":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                situationProfile: event.profile,
            }));
        case "conversation_was_ungrounded":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                situationProfile: { type: "no_situation_profile" },
            }));
        case "conversation_was_selected":
            return state.selectedConversationId === event.conversationId
                ? state
                : { ...state, selectedConversationId: event.conversationId };
        case "mentions_edited":
            return { ...state, composerMentions: event.mentions };
        case "grounding_source_pinned":
            return withGrounding(state, (map) =>
                addPendingGrounding(map, event.conversationId, event.source),
            );
        case "grounding_source_unpinned":
            return withGrounding(state, (map) =>
                removePendingGrounding(
                    map,
                    event.conversationId,
                    event.sourceId,
                ),
            );
        case "grounding_sources_cleared":
            return withGrounding(state, (map) =>
                clearPendingGrounding(map, event.conversationId),
            );
        case "pending_grounding_was_flushed":
            return withGrounding(state, (map) =>
                clearPendingSituation(
                    clearPendingGrounding(map, event.conversationId),
                    event.conversationId,
                ),
            );
        case "turn_held_for_uploads":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                heldSend: {
                    type: "awaiting_uploads",
                    entryId: event.entryId,
                    send: event.send,
                    standing: event.standing,
                },
            }));
        case "turn_held_for_start":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                heldSend: { type: "awaiting_start", send: event.send },
            }));
        case "conversation_load_began":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                transcriptLoad: "requested",
            }));
        case "conversation_was_loaded":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                conversation: mergeInFlightTranscript(
                    session.drafting
                        ? withoutUnfinishedRows(event.entries)
                        : event.entries,
                    session.conversation,
                ),
            }));
        case "conversation_load_had_error":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                transcriptLoad: "unrequested",
            }));
        case "held_upload_settled":
            return withSession(state, event.conversationId, (session) => {
                const held = session.heldSend;
                if (held.type !== "awaiting_uploads") return session;
                const settle = (mentions: readonly ComposerResourceRef[]) =>
                    settleUploadChip(mentions, event.chipId, event.outcome);
                return {
                    ...session,
                    heldSend: {
                        ...held,
                        send: {
                            ...held.send,
                            mentions: settle(held.send.mentions),
                        },
                    },
                    conversation: session.conversation.map((entry) =>
                        entry.type === "author" &&
                        entry.id === held.entryId &&
                        entry.mentions !== undefined
                            ? { ...entry, mentions: settle(entry.mentions) }
                            : entry,
                    ),
                };
            });
        case "held_turn_released":
            return withSession(state, event.conversationId, (session) => ({
                ...session,
                heldSend: { type: "no_held_send" },
            }));
        default:
            return exhaustSwitchCase(event);
    }
};

// Fold one session through, preserving the rest of the map (the existing
// `updateChatSession` helper — creates an empty session on first touch).
const withSession = (
    state: ChatState,
    conversationId: string,
    update: (session: ChatSession) => ChatSession,
): ChatState => ({
    ...state,
    sessions: updateChatSession(state.sessions, conversationId, update),
});

// Fold the pending-grounding map; the transitions return the SAME map on a
// no-op, so state identity is kept then too.
const withGrounding = (
    state: ChatState,
    update: (map: PendingGrounding) => PendingGrounding,
): ChatState => {
    const next = update(state.pendingGrounding);
    return next === state.pendingGrounding
        ? state
        : { ...state, pendingGrounding: next };
};

// Fold a whole decision's events onto state — the shell's commit step.
export const evolveChatAll = (
    state: ChatState,
    events: readonly ChatEvent[],
): ChatState => events.reduce(evolveChat, state);

// Texts added to the composer one per line, below any draft, never replacing
// it (PS-61: Edit, and a refused waiting message coming back).
const withLines = (draft: string, texts: readonly string[]): string =>
    [...(draft.trim() === "" ? [] : [draft]), ...texts].join("\n");
